import { ICellRendererParams } from 'ag-grid-community';
import { Tooltip } from 'antd';
import { FC, useEffect, useState } from 'react';
import { WHERE } from 'src/utils/firebase/firebase-manager';

import useSWRProducts from 'src/hooks/swr/useSWRProducts';

interface AgCellInventoryTooltipProps extends ICellRendererParams {
  value: string;
}

/**
 * 재고명 셀에 마우스 오버시 툴팁으로 상품 정보를 보여주는 컴포넌트입니다.
 */
const AgCellInventoryTooltip: FC<AgCellInventoryTooltipProps> = (props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);
  const [shouldFetch, setShouldFetch] = useState<boolean>(false);

  // 마우스 오버시에만 데이터를 로드하기 위해 조건부로 where 값을 설정합니다
  const where: WHERE[] | null = shouldFetch ? [['inventoryId', '==', props.data._id]] : null;
  const { products, isLoading } = useSWRProducts(where);

  // loading 상태 관리
  useEffect(() => {
    if (shouldFetch) {
      setLoading(isLoading);
    }
  }, [isLoading, shouldFetch]);

  // 마우스 오버시에만 데이터를 로드합니다.
  const handleMouseEnter = () => {
    setTooltipVisible(true);
    // 데이터를 아직 로드하지 않았다면 로드 시작
    if (!shouldFetch) {
      setShouldFetch(true);
    }
  };

  const handleMouseLeave = () => {
    setTooltipVisible(false);
  };

  // 툴팁 내용 구성
  const tooltipContent = () => {
    if (loading) {
      return <div>데이터 로딩 중...</div>;
    }

    if (!products || products.length === 0) {
      return <div>이 재고를 사용하는 상품이 없습니다.</div>;
    }

    return (
      <div style={{ maxWidth: '360px', maxHeight: '400px', overflow: 'auto' }}>
        {products
          .sort((a, b) => {
            // box 패킹은 맨 뒤로 정렬
            if (a.packing === 'box' && b.packing !== 'box') return 1;
            if (a.packing !== 'box' && b.packing === 'box') return -1;
            // 그 외에는 이름순 정렬
            return a.name.localeCompare(b.name);
          })
          .map((product) => (
            <div key={product._id} style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
              {product.thumbnail ? (
                <img
                  src={product.thumbnail}
                  alt={product.name}
                  style={{ width: '50px', height: '50px', marginRight: '10px', objectFit: 'cover' }}
                />
              ) : (
                <div style={{ width: '50px', height: '50px', backgroundColor: '#f0f0f0', marginRight: '10px' }} />
              )}
              <div>
                <strong>{product.fullName}</strong>
              </div>
            </div>
          ))}
      </div>
    );
  };

  return (
    <Tooltip
      title={tooltipContent}
      open={tooltipVisible}
      onOpenChange={(open) => (open ? handleMouseEnter() : handleMouseLeave())}>
      <div style={{ cursor: 'pointer' }}>{props.value}</div>
    </Tooltip>
  );
};

export default AgCellInventoryTooltip;
